import { useEffect, useState } from "react";

import { css } from "@compiled/react";

import { wacToken } from "@/libraries/design/foundations";

import { ANIMATED_CARD_ID } from "../constants/ui";
import {
    colors,
    getAssetForEpicLink,
    getAssetOptionsForAssignee,
    getAssetOptionsForIssueType,
    getAssetOptionsForPriority
} from "../helpers";
import { useAnimatedJiraBoardStore, useMessages } from "../state";

export const Card = ({
    summary,
    epicName,
    issueType,
    priority,
    assignee,
    id,
    role
}: cardProps) => {
    const [isAnimatedCardId, setIsAnimatedCardId] = useState(false);
    const [messages] = useMessages();
    const [, { nextAnimationStep }] = useAnimatedJiraBoardStore();
    const epicLinkElement = getAssetForEpicLink(epicName, messages);
    const issueTypeElement = getAssetOptionsForIssueType(
        issueType,
        messages,
        true
    );
    useEffect(() => {
        if (id === ANIMATED_CARD_ID) {
            setIsAnimatedCardId(true);
            setTimeout(() => {
                nextAnimationStep();
            }, 4250);
        }
    }, [id, nextAnimationStep]);

    const priorityElement = getAssetOptionsForPriority(
        priority,
        messages,
        true
    );
    const assigneeElement = getAssetOptionsForAssignee(
        assignee,
        messages,
        true
    );

    const cardStyles = css({
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        color: colors.text,
        margin: "5px 0px",
        padding: "12px",
        borderRadius: "4px",
        background: wacToken("color.neutral.light0"),
        boxShadow: `0px 1px 1px #091E4240, 0px 0px 1px #091E424F`,
        gap: "8px"
    });
    const labelContainer = css({
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: "400",
        lineHeight: "1.42857143",
        width: "90%"
    });
    const epicLinkRow = css({
        display: "flex",
        flexDirection: "row"
    });
    const issueTypeContainer = css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "5px"
    });
    const typePriorityAssigneeRow = css({
        display: "flex",
        flexDirection: "row"
    });
    const priorityAssigneeContainer = css({
        display: "flex",
        flexDirection: "row",
        gap: "8px",
        marginInlineStart: "auto"
    });
    const animateCardonRender = css({
        ["@keyframes fadeOut"]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["from"]: {
                opacity: 1
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["to"]: {
                opacity: 0
            }
        },
        ["@keyframes grow"]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["from"]: {
                maxHeight: "0px",
                padding: "0px 12px"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["to"]: {
                maxHeight: "106px",
                padding: "12px"
            }
        },
        ["@keyframes shrink"]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["from"]: {
                maxHeight: "106px",
                padding: "12px"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["to"]: {
                maxHeight: "0px",
                padding: "0px 12px",
                margin: "0px"
            }
        },
        ["@keyframes borderPulse"]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            from: {
                boxShadow: `0px 0px 0px 3px ${colors.textSelected}, 0 0 0 0px rgba(12, 102, 228, 0.5)`
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            to: {
                boxShadow: `0px 0px 0px 3px ${colors.textSelected}, 0 0 0 20px rgba(12, 102, 228, 0)`
            }
        },
        maxHeight: "0px",
        overflow: "hidden",
        whiteSpace: "nowrap",
        animation:
            "1s grow forwards ease, 1s shrink 3s forwards ease, 1s borderPulse infinite, .5s fadeOut 3.25s forwards",
        padding: "0px 12px",
        boxShadow: `0px 0px 0px 3px ${colors.textSelected}`
    });
    return (
        <div
            css={[cardStyles, isAnimatedCardId && animateCardonRender]}
            aria-describedby={id}
            role={role}
        >
            <div css={labelContainer} id={id}>
                {summary}
            </div>
            <div css={epicLinkRow}>{epicLinkElement}</div>
            <div css={typePriorityAssigneeRow}>
                <div css={issueTypeContainer}>{issueTypeElement}</div>
                <div css={priorityAssigneeContainer}>
                    <div>{priorityElement}</div>
                    <div>{assigneeElement}</div>
                </div>
            </div>
        </div>
    );
};

export type cardProps = {
    summary?: string;
    epicName?: string;
    issueType?: string;
    priority?: string;
    assignee?: string;
} & React.HTMLAttributes<HTMLDivElement>;
